import {FunctionsHttpError} from '@supabase/supabase-js';
import {useState} from 'react';
import {Link} from 'react-router';

import {usdFormatter} from '@/lib/helpers';
import {SupabaseInstance} from '@/lib/utils';

import {useAuth} from '@/providers/components/authProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

import {ConfirmDeleteModalComponent} from '@/components';

const withdrawSuggestions = [
  {value: 100, label: '$100'},
  {value: 500, label: '$500'},
  {value: 1000, label: '$1,000'},
  {value: 5000, label: '$5,000'},
];

function WithdrawCard() {
  const {accountBalance, getUserAccountBalance} = useAuth();
  const {toast} = useNotifications();

  // State
  const [withdrawAmount, setWithdrawAmount] = useState<number | ''>('');
  const [loading, setLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const withdrawFunds = async () => {
    if (!withdrawAmount) return;

    setLoading(true);
    try {
      const {data: user} = await SupabaseInstance.supabase.auth.getUser();
      if (!user.user) return;
      const {error} = await SupabaseInstance.supabase.functions.invoke('withdraw-funds', {
        body: {
          user_uid: user.user.id,
          withdraw_amount: withdrawAmount,
        },
      });

      if (error && error instanceof FunctionsHttpError) {
        const {
          error: {message},
        } = await error.context.json();

        toast.error(message);
      }

      if (error) {
        console.error(error);
      } else {
        await getUserAccountBalance();
        setWithdrawAmount(''); // Clear amount after successful withdrawal
        toast.success('Withdrawal successful');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {' '}
      <div className="box custom-box">
        <div className="box-header">
          <div className="box-title">Withdraw</div>
        </div>
        <div className="box-body">
          <div className="tab-pane border-0 !p-0" id="sell-crypto" role="tabpanel" aria-labelledby="sell-crypto">
            <div className="mb-4">
              <span className="form-label">Account Balance :</span>
              <div className="relative">
                <Link aria-label="anchor" to="#" className="stretched-link"></Link>
                <div className="mt-1 flex items-center justify-between gap-4 rounded-md border p-2 dark:border-defaultborder/10">
                  <div className="flex items-center gap-2">
                    <div className="leading-none">
                      <span className="avatar bg-light !p-0">
                        <img
                          className="!object-contain"
                          src="/images/brand-logos/tnc_logo.png"
                          alt="token holding flag"
                        />
                      </span>
                    </div>
                    <div className="font-semibold">Account Balance</div>
                  </div>
                  <div className="text-end">
                    <span className="block font-semibold">{usdFormatter.format(accountBalance)}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-4">
              <span className="form-label">Withdraw To :</span>
              <div className="relative">
                <Link aria-label="anchor" to="#" className="stretched-link"></Link>
                <div className="mt-1 flex items-center gap-2 rounded-md border p-2 dark:border-defaultborder/10">
                  <div className="leading-none">
                    <span className="avatar bg-light p-2">
                      <i className="ri-bank-line text-xl text-primary"></i>
                    </span>
                  </div>
                  <div>
                    <span className="block font-semibold">Bank Account</span>
                    <span className="text-[#8c9097] dark:text-white/50">Select Account...</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-4">
              <span className="block font-semibold">Amount :</span>
              <div className="flex items-center justify-between text-[#8c9097] dark:text-white/50">
                <div>Available to Withdraw</div>
                <div>{usdFormatter.format(accountBalance)}</div>
              </div>
            </div>
            <div className="input-group mb-4 block !flex-nowrap sm:flex">
              <input
                type="number"
                className="form-control border-e-0"
                aria-label="withdraw amount"
                placeholder="Enter amount"
                max={accountBalance}
                min={0}
                value={withdrawAmount}
                onChange={e => setWithdrawAmount(Number(e.target.value))}
              />
              <div className="input-group-text">USD</div>
            </div>

            <div className="mb-4 flex flex-wrap gap-2">
              {withdrawSuggestions.map(suggestion => (
                <button
                  key={suggestion.value}
                  className="ti-btn ti-btn-outline-primary ti-btn-wave"
                  onClick={() => {
                    setWithdrawAmount(suggestion.value);
                  }}
                >
                  {suggestion.label}
                </button>
              ))}
            </div>

            <div className="mt-6 grid">
              <button
                type="button"
                className="ti-btn ti-btn-primary-full btn-wave ti-btn-lg dark:border-defaultborder/10"
                onClick={() => setIsVisible(true)}
                disabled={!withdrawAmount || loading}
              >
                {loading ? 'Withdrawing...' : 'Withdraw'}
              </button>
            </div>
          </div>
        </div>
      </div>
      <ConfirmDeleteModalComponent
        description={`Are you sure you want to withdraw ${withdrawAmount} to your bank account?`}
        isVisible={isVisible}
        onClose={() => setIsVisible(false)}
        onConfirm={() => {
          withdrawFunds();
          setIsVisible(false);
        }}
        title="Withdraw Funds"
        btnText="Withdraw"
      />
    </>
  );
}

export default WithdrawCard;
